// The SKFitter lattice views: the cell of the DFT calculation (step 1), the neighbours a set-up includes (step 2)
// and the fitted parameters on the lattice (step 3). Server: /skfitter/structure, /neighbours, /parammap.
import { Alert, Badge, Group, NumberInput, Paper, ScrollArea, Select, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { LatticeBox, Legend } from "../../components/Lattice";
import { limitError } from "../../components/RunCard";
import {
  clampRepeat, countText, defaultRepeat, isLimited, modelScene, neighbourView, num, paramMap, shellColor, siteLine,
  structureView, supercellAtoms, supercellBonds, vec, type DrawAtom, type ModelBond, type NeighbourView, type ParamMap,
  type StructureView, type Vec3,
} from "../../lib/lattice";
import type { Cfg } from "../../lib/skfitter";

export const useStructure = (jobId: string) =>
  useQuery({ queryKey: ["sk-structure", jobId], queryFn: () => structureView(jobId), staleTime: Infinity });

const AXES = ["a", "b", "c"];
/** A 2D cell is first seen from above (along its vacuum axis), a 3D cell in perspective. */
export const naturalView = (sv: StructureView) =>
  (sv.vacuum_axes.length === 1 ? (["a", "b", "c"][sv.vacuum_axes[0]] as "a" | "b" | "c") : "free");
const fmtE = (v: number | null | undefined) => (v == null ? "–" : `${v.toFixed(4)} eV`);

function LimitNote({ limit, what }: { limit: Parameters<typeof limitError>[0]; what: string }) {
  return (
    <Stack gap={6}>
      <ErrorBox error={limitError(limit)} />
      <Text size="sm" c="dimmed">{what} is not drawn on your plan; the numbers below are still shown.</Text>
    </Stack>
  );
}

// == step 1: the cell of the DFT calculation ===========================================================================
export function StructureTab({ jobId }: { jobId: string }) {
  const q = useStructure(jobId);
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Text size="sm" c="dimmed">Reading the structure …</Text>;
  return <StructurePanel sv={q.data} />;
}

/** A cell drawn in 3D with its numbers (lattice, space group, sites): SKFitter's DFT step, and the DFT module. */
export function StructurePanel({ sv, onPick, picked, hideInfo }: {
  sv: StructureView;
  onPick?: (site: number) => void;        // a click on an atom: its site (0-based, the order of sv.sites)
  picked?: number | null;                 // the site drawn as selected
  hideInfo?: boolean;
}) {
  return (
    <Stack gap="sm" data-testid="structure-tab">
      {sv.limit ? <LimitNote limit={sv.limit} what="The lattice" /> : <StructureScene sv={sv} onPick={onPick} picked={picked} />}
      {!hideInfo && <StructureInfo sv={sv} />}
    </Stack>
  );
}

function StructureScene({ sv, onPick, picked }: { sv: StructureView; onPick?: (site: number) => void; picked?: number | null }) {
  const L = sv.limits;
  const [repeat, setRepeat] = useState<Vec3>(() => defaultRepeat(sv));
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [maxShell, setMaxShell] = useState(1);
  const shown = useMemo(() => new Set(sv.elements.map((e) => e.symbol).filter((e) => !hidden.has(e))), [sv, hidden]);
  const atoms = useMemo(() => supercellAtoms(sv, repeat, shown), [sv, repeat, shown]);
  const allBonds = useMemo(() => (maxShell > 0 ? supercellBonds(sv.bonds, atoms, maxShell) : []), [sv, atoms, maxShell]);
  const tooMany = atoms.length > L["skfitter.view_max_atoms"];
  const bonds = allBonds.length > L["skfitter.view_max_bonds"] ? [] : allBonds;
  const shells = [{ value: "0", label: "No bonds" },
    ...sv.shells.map((d, i) => ({ value: String(i + 1), label: `Shell ${i + 1} (${d.toFixed(3)} Å)` }))];
  const setR = (k: number, v: number | string) => {
    const n = [...repeat] as Vec3;
    n[k] = typeof v === "number" ? v : 1;
    setRepeat(clampRepeat(n, L["skfitter.view_max_repeat"]));
  };
  const site = (a: DrawAtom) => sv.sites[a.site];
  return (
    <LatticeBox atoms={tooMany ? [] : atoms} bonds={tooMany ? [] : bonds} lattice={sv.lattice} repeat={repeat} halfBonds
      pngName={`${sv.formula}_structure`} height={460}
      onAtomClick={onPick ? (a) => onPick(a.site) : undefined}
      selectedAtom={picked != null ? atoms.find((a) => a.site === picked)?.key ?? null : undefined}
      defaultView={naturalView(sv)}
      tooltip={(a) => {
        const s = site(a);
        return (<div><b>{s.label}</b> ({s.symbol}) · cell ({a.cell.join(", ")})<br />frac {vec(s.frac, 4)}<br />
          {s.eq_label !== s.label && <>equivalent to {s.eq_label}<br /></>}{s.moment != null && <>{sv.moment_label ? `${sv.moment_label} ${s.moment.toFixed(2)}` : `initial moment (MAGMOM) ${s.moment.toFixed(2)} μB`}</>}</div>);
      }}
      controls={<>
        {[0, 1, 2].map((k) => (
          <NumberInput key={k} size="xs" w={62} label={`n${AXES[k]}`} min={1} max={L["skfitter.view_max_repeat"]} value={repeat[k]}
            onChange={(v) => setR(k, v)} aria-label={`Repeat along ${AXES[k]}`} />
        ))}
        <Select size="xs" w={170} label="Bonds up to" data={shells} value={String(maxShell)} allowDeselect={false}
          onChange={(v) => v != null && setMaxShell(Number(v))} />
      </>}
      legend={<>
        <Legend title="Elements" items={sv.elements.map((e) => ({ key: e.symbol, label: `${e.symbol} (${e.count})`, color: e.color,
          off: hidden.has(e.symbol), onClick: () => setHidden((h) => { const n = new Set(h); if (n.has(e.symbol)) n.delete(e.symbol); else n.add(e.symbol); return n; }) }))} />
        <Text size="xs" c="dimmed" data-testid="structure-counts">
          {repeat.join(" × ")} cells · {atoms.length} atoms · {bonds.length} bonds drawn
          {` (plan: up to ${L["skfitter.view_max_atoms"]} atoms, ${L["skfitter.view_max_bonds"]} bonds, ${L["skfitter.view_max_repeat"]} repeats per axis)`}
        </Text>
        {tooMany && <Alert color="orange" p="xs"><Text size="xs">{atoms.length} atoms is more than your plan draws ({L["skfitter.view_max_atoms"]}): use fewer repeats.</Text></Alert>}
        {allBonds.length > L["skfitter.view_max_bonds"] && <Alert color="orange" p="xs"><Text size="xs">{allBonds.length} bonds is more than your plan draws ({L["skfitter.view_max_bonds"]}): choose fewer shells or repeats.</Text></Alert>}
      </>}
    />
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper withBorder p="xs" radius="sm">
      <Text size="sm" fw={600} mb={4}>{title}</Text>
      {children}
    </Paper>
  );
}

function KV({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <Table withRowBorders={false} verticalSpacing={1} horizontalSpacing={4}>
      <Table.Tbody>
        {rows.map(([k, v]) => <Table.Tr key={k}><Table.Td><Text size="xs" c="dimmed">{k}</Text></Table.Td><Table.Td><Text size="xs">{v}</Text></Table.Td></Table.Tr>)}
      </Table.Tbody>
    </Table>
  );
}

export function StructureInfo({ sv }: { sv: StructureView }) {
  const vac = sv.vacuum_axes.map((k) => AXES[k]).join(", ");
  return (
    <Stack gap="xs" data-testid="structure-info">
      <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="xs">
        <InfoCard title="Crystal">
          <KV rows={[
            ["Formula", sv.formula], ["Space group", `${sv.spacegroup.symbol}${sv.spacegroup.number ? ` (No. ${sv.spacegroup.number})` : ""}`],
            ["Crystal system", sv.spacegroup.crystal_system || "–"],
            ["Atoms in the cell", `${sv.n_sites} (${sv.n_inequivalent} inequivalent)`],
            ["Dimensionality", vac ? `${sv.dimensionality} (vacuum along ${vac})` : sv.dimensionality],
            ["Volume", `${sv.volume.toFixed(3)} Å³`], ["Density", `${sv.density.toFixed(3)} g/cm³`],
          ]} />
        </InfoCard>
        <InfoCard title="Lattice constants">
          <KV rows={[
            ["a, b, c", `${sv.abc.map((v) => v.toFixed(4)).join(", ")} Å`],
            ["α, β, γ", `${sv.angles.map((v) => v.toFixed(3)).join("°, ")}°`],
            ["Nearest neighbour d₀", sv.d0 == null ? "–" : `${sv.d0.toFixed(4)} Å`],
            ["Shells", sv.shells.slice(0, 6).map((d) => d.toFixed(3)).join(", ") + (sv.shells.length > 6 ? ", …" : "") + " Å"],
          ]} />
        </InfoCard>
        <InfoCard title="Lattice vectors (Cartesian, Å)">
          <KV rows={sv.lattice.map((v, i) => [`a${i + 1}`, vec(v, 4)] as [string, string])} />
          <Text size="xs" c="dimmed" mt={4}>Reciprocal vectors (1/Å, with 2π)</Text>
          <KV rows={sv.reciprocal.map((v, i) => [`b${i + 1}`, vec(v, 4)] as [string, string])} />
        </InfoCard>
      </SimpleGrid>
      <InfoCard title="Atoms in the unit cell">
        <ScrollArea.Autosize mah={240}>
          <Table striped verticalSpacing={1} fz="xs" data-testid="sites-table">
            <Table.Thead><Table.Tr><Table.Th>Site</Table.Th><Table.Th>Element</Table.Th><Table.Th>Fractional (a, b, c)</Table.Th>
              <Table.Th>Cartesian (x, y, z) Å</Table.Th><Table.Th>Equivalent to</Table.Th>{sv.sites.some((s) => s.moment != null) && <Table.Th>{sv.moment_label ? sv.moment_label[0].toUpperCase() + sv.moment_label.slice(1) : "Initial moment (μB)"}</Table.Th>}</Table.Tr></Table.Thead>
            <Table.Tbody>
              {sv.sites.map((s) => (
                <Table.Tr key={s.index}>
                  <Table.Td>{s.label}</Table.Td><Table.Td>{s.symbol}</Table.Td><Table.Td ff="monospace">{vec(s.frac, 5)}</Table.Td>
                  <Table.Td ff="monospace">{vec(s.cart, 4)}</Table.Td><Table.Td>{s.eq_label}</Table.Td>
                  {sv.sites.some((x) => x.moment != null) && <Table.Td>{s.moment == null ? "–" : s.moment.toFixed(2)}</Table.Td>}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      </InfoCard>
      <Text size="xs" c="dimmed">This is the cell of the DFT calculation; the repeats only draw more cells. Shells are the distinct
        atom–atom distances of the whole cell (to 0.001 Å) - the ones the fit counts in “Neighbour shells”.</Text>
    </Stack>
  );
}

// == step 2: the neighbours of the set-up =============================================================================
export function NeighboursTab({ jobId, cfg }: { jobId: string; cfg: Cfg }) {
  const st = useStructure(jobId);
  const [debounced] = useDebouncedValue(JSON.stringify(cfg), 300);
  const q = useQuery({ queryKey: ["sk-neighbours", jobId, debounced], queryFn: () => neighbourView(jobId, JSON.parse(debounced)),
    placeholderData: (p) => p });
  const [centre, setCentre] = useState<number | null>(0);
  if (q.error || st.error) return <ErrorBox error={q.error ?? st.error} />;
  if (!q.data || !st.data) return <Text size="sm" c="dimmed">Finding the neighbours …</Text>;
  if (isLimited(q.data)) return <ErrorBox error={limitError(q.data.limit)} />;
  const nv = q.data as NeighbourView;
  const sv = st.data;
  return <NeighbourScene sv={sv} nv={nv} centre={centre} setCentre={setCentre} />;
}

function NeighbourScene({ sv, nv, centre, setCentre }: { sv: StructureView; nv: NeighbourView; centre: number | null; setCentre: (c: number | null) => void }) {
  const c = centre != null && centre < sv.n_sites ? centre : null;
  const scene = useMemo(() => modelScene(sv, nv.bonds, c, shellColor), [sv, nv, c]);
  const usedShells = [...new Set(scene.bonds.map((b) => b.shell))].sort((a, b) => a - b);
  const options = [{ value: "all", label: "All atoms of the cell" },
    ...sv.sites.map((s) => ({ value: String(s.index), label: `${s.label}${s.eq_label !== s.label ? ` (≡ ${s.eq_label})` : ""}` }))];
  const ineq = nv.sites.filter((s) => s.eq === s.index);
  const tooMany = scene.atoms.length > sv.limits["skfitter.view_max_atoms"] || scene.bonds.length > sv.limits["skfitter.view_max_bonds"];
  return (
    <Stack gap="sm" data-testid="neighbours-tab">
      <LatticeBox atoms={tooMany ? [] : scene.atoms} bonds={tooMany ? [] : scene.bonds} lattice={sv.lattice} pngName={`${sv.formula}_neighbours`}
        height={420} defaultView={naturalView(sv)} selectedAtom={c != null ? `${c}@0,0,0` : null}
        onAtomClick={(a) => setCentre(a.site)}
        tooltip={(a) => <span><b>{a.label}</b>{a.ghost ? " (in a neighbouring cell)" : ""} - click to centre</span>}
        controls={<Select size="xs" w={190} label="Neighbours of" data={options} value={c == null ? "all" : String(c)} allowDeselect={false}
          onChange={(v) => setCentre(v == null || v === "all" ? null : Number(v))} />}
        legend={<>
          <Legend title="Bonds by shell" items={usedShells.map((s) => ({ key: String(s), label: `${s}: ${nv.shells[s - 1]?.toFixed(3) ?? "?"} Å`,
            color: shellColor(s), ring: true }))} />
          <Legend title="Elements" items={sv.elements.map((e) => ({ key: e.symbol, label: e.symbol, color: e.color }))} />
          {tooMany && <Alert color="orange" p="xs"><Text size="xs">Too many bonds to draw on your plan - pick one atom.</Text></Alert>}
        </>} />
      <Stack gap="xs">
        <Group gap={6} wrap="wrap">
          <Badge variant="light">{nv.n_shells} shell{nv.n_shells > 1 ? "s" : ""}</Badge>
          <Badge variant="light" color="gray">cutoff {nv.cutoff.toFixed(3)} Å</Badge>
          <Badge variant="light" color="gray">d₀ {nv.d0.toFixed(3)} Å</Badge>
          <Badge variant="light" color="gray">{nv.n_pairs} neighbour pairs</Badge>
        </Group>
        <Text size="xs" c="dimmed">Shells are the distinct distances over the whole cell; the cutoff is shell {nv.n_shells} + 0.1 Å.
          {nv.distance_decay ? " Hoppings beyond d₀ are scaled as V(r) = V₀ (d₀ / r)^η." : " One shell: each hopping is a single value V₀."}</Text>
        {nv.warnings.map((w) => <Alert key={w} color="orange" p="xs"><Text size="xs">{w}</Text></Alert>)}
        <Paper withBorder p="xs">
          <Text size="sm" fw={600} mb={4}>Neighbours per atom</Text>
          <Stack gap={6} data-testid="neighbour-lines">
            {ineq.map((s) => (
              <div key={s.index}>
                <Text size="xs" fw={600} style={{ cursor: "pointer" }} onClick={() => setCentre(s.index)}>{s.label}
                  {nv.sites.filter((o) => o.eq === s.index && o.index !== s.index).length > 0 &&
                    <Text span c="dimmed" fw={400}> (also {nv.sites.filter((o) => o.eq === s.index && o.index !== s.index).map((o) => o.label).join(", ")})</Text>}</Text>
                {s.shells.map((r) => (
                  <Group key={r.shell} gap={6} wrap="nowrap">
                    <span style={{ width: 10, height: 10, background: shellColor(r.shell), borderRadius: 2, display: "inline-block", flex: "none" }} />
                    <Text size="xs">shell {r.shell}: {countText(r.by_element)} at {r.dist.toFixed(3)} Å{r.beyond ? " (by the 0.1 Å margin)" : ""}</Text>
                  </Group>
                ))}
                {s.missing_shells.length > 0 && <Text size="xs" c="dimmed">no neighbour in shell {s.missing_shells.join(", ")}</Text>}
              </div>
            ))}
          </Stack>
          <Text hidden data-testid="neighbour-text">{ineq.map(siteLine).join("\n")}</Text>
        </Paper>
        <Paper withBorder p="xs">
          <Text size="sm" fw={600} mb={4}>Hopping integrals these bonds bring in ({nv.hopping_keys.length})</Text>
          <Text size="xs" ff="monospace">{nv.hopping_keys.map((k) => `${k.key} (${k.label})`).join(", ") || "none"}</Text>
        </Paper>
      </Stack>
    </Stack>
  );
}

// == step 3: the fitted parameters on the lattice ====================================================================
export type LatticePick = { kind: "bond" | "site"; index: number };
export const useParamMap = (jobId: string, fitJobId: string) =>
  useQuery({ queryKey: ["sk-parammap", jobId, fitJobId], queryFn: () => paramMap(jobId, fitJobId), staleTime: Infinity,
    enabled: !!jobId && !!fitJobId });

export function ParamLattice({ jobId, fitJobId, selected, onSelect, highlight }: {
  jobId: string; fitJobId: string; selected: LatticePick | null; onSelect: (s: LatticePick | null) => void;
  highlight?: Set<number>;                 // bonds that use the parameter picked in the table
}) {
  const st = useStructure(jobId);
  const q = useParamMap(jobId, fitJobId);
  const pm = q.data && !isLimited(q.data) ? (q.data as ParamMap) : undefined;
  const scene = useMemo(() => (pm && st.data ? modelScene(st.data, pm.bonds, null, shellColor) : null), [pm, st.data]);
  if (q.error || st.error) return <ErrorBox error={q.error ?? st.error} />;
  if (q.data && isLimited(q.data)) return <ErrorBox error={limitError(q.data.limit)} />;
  if (!pm || !st.data || !scene) return <Text size="sm" c="dimmed">Placing the parameters on the lattice …</Text>;
  const sv = st.data;
  const hi = selected?.kind === "bond" ? new Set([selected.index]) : highlight;
  const site = (a: DrawAtom) => pm.sites[a.site];
  const shells = [...new Set(pm.bonds.map((b) => b.shell))].sort((a, b) => a - b);
  return (
    <Stack gap={6} data-testid="param-lattice">
      <LatticeBox atoms={scene.atoms} bonds={scene.bonds} lattice={sv.lattice} pngName={`${sv.formula}_parameters`} height={380}
        defaultView={naturalView(sv)}
        highlightBonds={hi} dimOthers selectedAtom={selected?.kind === "site" ? `${selected.index}@0,0,0` : null}
        onAtomClick={(a) => onSelect({ kind: "site", index: a.site })}
        onBondClick={(b) => onSelect(b == null ? null : { kind: "bond", index: b })}
        tooltip={(a) => <OnsiteTip s={site(a)} ghost={!!a.ghost} />}
        legend={<Legend title="Bonds by shell" items={shells.map((s) => ({ key: String(s), label: `${s}: ${pm.shells[s - 1]?.toFixed(3)} Å`, color: shellColor(s), ring: true }))} />}
        footer={<Text size="xs" c="dimmed">Hover an atom for its on-site parameters; click a bond (or near one) for its hoppings.</Text>} />
      {selected?.kind === "bond" && pm.bonds[selected.index] && <BondCard b={pm.bonds[selected.index]} pm={pm} />}
      {selected?.kind === "site" && pm.sites[selected.index] && <SiteCard s={pm.sites[selected.index]} />}
    </Stack>
  );
}

function OnsiteTip({ s, ghost }: { s: ParamMap["sites"][number]; ghost: boolean }) {
  return (
    <div data-testid="onsite-tip">
      <b>{s.label}</b>{s.eq_label !== s.label ? ` (≡ ${s.eq_label})` : ""}{ghost ? " · neighbouring cell" : ""}
      <table style={{ borderSpacing: "8px 0", marginLeft: -8 }}><tbody>
        {s.onsite.map((r) => <tr key={r.key}><td>E({r.orbital})</td><td style={{ textAlign: "right" }}>{fmtE(r.value)}</td></tr>)}
        {s.soc.map((r) => <tr key={r.key}><td>λ<sub>{r.family}</sub> (spin–orbit)</td><td style={{ textAlign: "right" }}>{fmtE(r.value)}</td></tr>)}
        {s.exchange.map((r) => <tr key={r.key}><td>Δ<sub>{r.family}</sub> (magnetic) {r.sign < 0 ? "↓" : "↑"}</td><td style={{ textAlign: "right" }}>{fmtE(r.value)}</td></tr>)}
      </tbody></table>
    </div>
  );
}

function SiteCard({ s }: { s: ParamMap["sites"][number] }) {
  return (
    <Paper withBorder p="xs" data-testid="site-card">
      <Text size="sm" fw={600}>{s.label} - on-site parameters{s.eq_label !== s.label ? ` (shared with ${s.eq_label})` : ""}</Text>
      <Table verticalSpacing={1} fz="xs">
        <Table.Tbody>
          {s.onsite.map((r) => <Table.Tr key={r.key}><Table.Td>Orbital energy E({r.orbital})</Table.Td><Table.Td ff="monospace">{r.key}</Table.Td><Table.Td>{fmtE(r.value)}</Table.Td></Table.Tr>)}
          {s.soc.map((r) => <Table.Tr key={r.key}><Table.Td>Spin–orbit λ ({r.family})</Table.Td><Table.Td ff="monospace">{r.key}</Table.Td><Table.Td>{fmtE(r.value)}</Table.Td></Table.Tr>)}
          {s.exchange.map((r) => <Table.Tr key={r.key}><Table.Td>Exchange splitting Δ ({r.family}), moment {r.sign < 0 ? "↓" : "↑"}: ±Δ/2 = {num(r.site_value)} eV</Table.Td><Table.Td ff="monospace">{r.key}</Table.Td><Table.Td>{fmtE(r.value)}</Table.Td></Table.Tr>)}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}

function BondCard({ b, pm }: { b: ModelBond; pm: ParamMap }) {
  return (
    <Paper withBorder p="xs" data-testid="bond-card">
      <Text size="sm" fw={600}>{b.label} · shell {b.shell} · r = {b.dist.toFixed(4)} Å{b.image.some((v) => v !== 0) ? ` · to cell (${b.image.join(", ")})` : ""}</Text>
      <Text size="xs" c="dimmed">{pm.distance_decay ? `V(r) = V₀ (d₀ / r)^η with d₀ = ${pm.d0.toFixed(4)} Å` : "One shell: V(r) = V₀"}</Text>
      <Table verticalSpacing={1} fz="xs">
        <Table.Thead><Table.Tr><Table.Th>Integral</Table.Th><Table.Th>V₀ (eV)</Table.Th>{pm.distance_decay && <Table.Th>η</Table.Th>}<Table.Th>V(r) (eV)</Table.Th>
          {pm.non_orthogonal && <><Table.Th>S₀</Table.Th><Table.Th>S(r)</Table.Th></>}</Table.Tr></Table.Thead>
        <Table.Tbody>
          {b.integrals.map((it) => (
            <Table.Tr key={it.key}>
              <Table.Td>{it.label} <Text span size="xs" c="dimmed" ff="monospace">{it.key}</Text></Table.Td>
              <Table.Td>{num(it.v0)}</Table.Td>{pm.distance_decay && <Table.Td>{num(it.eta, 3)}</Table.Td>}<Table.Td fw={600}>{num(it.value)}</Table.Td>
              {pm.non_orthogonal && <><Table.Td>{num(it.s0)}</Table.Td><Table.Td>{num(it.s_value)}</Table.Td></>}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
